import QueryBuilder, { Classname, Field, RuleGroupType, RuleType } from "react-querybuilder";
import { RuleSetMetadataEntry } from "../types/ruleSetTypes";
import ThemeProvider from "@mui/material/styles/ThemeProvider";
import { QueryBuilderMaterial } from "@react-querybuilder/material";
import { createTheme } from "@mui/material";
import "./RuleSetQueryBuilder.css";
import { useTranslation } from 'react-i18next';
import { getRuleMetadataName } from '../i18n/backendTranslations';

function getFields(metadata: RuleSetMetadataEntry[]) {
  return metadata.map((field: RuleSetMetadataEntry) => {
    return {
      name: field.id,
      label: getRuleMetadataName(field.id, field.displayName)
    };
  });
}

function getValueEditorType(field: string, operator: string, metadata: RuleSetMetadataEntry[]) {
  const metadataEntry = metadata.find((entry) => entry.id === field);

  if (!metadataEntry)
    throw new Error(`Metadata entry not found for field: ${field}`);

  switch(metadataEntry.valueType) {
    case "bool":
      return "select";
    case "int":
      return "text";
    default:
      return null;
  }
}

function getInputType(field: string, operator: string, metadata: RuleSetMetadataEntry[]) {
  const metadataEntry = metadata.find((entry) => entry.id === field);

  if (!metadataEntry)
    throw new Error(`Metadata entry not found for field: ${field}`);

  switch(metadataEntry.valueType) {
    case "int":
      return "number";
    default:
      return "text";
  }
}

function getValues(field: string, operator: string, metadata: RuleSetMetadataEntry[], t: (key: string) => string) {
  const metadataEntry = metadata.find((entry) => entry.id === field);

  if (!metadataEntry)
    throw new Error(`Metadata entry not found for field: ${field}`);

  switch(metadataEntry.valueType) {
    case "bool":
      return [
        { name: true, label: t('rules.true') },
        { name: false, label: t('rules.false') }
      ];
    default:
      return [];
  }
}

function getOperators(field: string, metadata: RuleSetMetadataEntry[], t: (key: string) => string) {
  const metadataEntry = metadata.find((entry) => entry.id === field);

  if (!metadataEntry)
    throw new Error(`Metadata entry not found for field: ${field}`);

  switch(metadataEntry.valueType) {
    case "bool":
      return [
        { name: "=", label: t('rules.is') }
      ];
    case "int":
      return [
        { name: "=", label: t('rules.equalTo') },
        { name: ">", label: t('rules.greaterThan') },
        { name: "<", label: t('rules.lessThan') },
        { name: ">=", label: t('rules.atLeast') },
        { name: "<=", label: t('rules.atMost') }
      ];
    default:
      return null;
  }
}

const muiTheme = createTheme();

export default function RuleSetQueryBuilder(props: {
  metadata: RuleSetMetadataEntry[],
  defaultQuery: RuleGroupType,
  readonly: boolean,
  onQueryChange?: (ruleGroup: RuleGroupType) => void,
  getRuleClassname?: (rule: RuleType, misc: { fieldData: Field }) => Classname
  getRuleGroupClassname?: (ruleGroup: RuleGroupType) => Classname
}) {
  const { t } = useTranslation();

  var controlClassnames = undefined;

  if (props.readonly) {
    controlClassnames = {
      addRule: "query-builder-hidden",
      addGroup: "query-builder-hidden",
      removeRule: "query-builder-hidden",
      removeGroup: "query-builder-hidden",
      header: "query-builder-hidden"
    }
  }

  return (
    <>
      <ThemeProvider theme={muiTheme}>
        <QueryBuilderMaterial>
          <QueryBuilder
            defaultQuery={props.defaultQuery}
            fields={getFields(props.metadata)}
            onQueryChange={props.onQueryChange}
            getOperators={(field) => getOperators(field, props.metadata, t)}
            getValueEditorType={(field, operator) => getValueEditorType(field, operator, props.metadata)}
            getInputType={(field, operator) => getInputType(field, operator, props.metadata)}
            getValues={(field, operator) => getValues(field, operator, props.metadata, t)}
            getRuleClassname={props.getRuleClassname}
            getRuleGroupClassname={props.getRuleGroupClassname}
            controlClassnames={controlClassnames}
            showCombinatorsBetweenRules={true}
            combinators={[
              { name: 'and', label: t('rules.builder.and') },
              { name: 'or', label: t('rules.builder.or') },
            ]}
            translations={{
              fields: { title: t('rules.builder.fields') },
              operators: { title: t('rules.builder.operators') },
              value: { title: t('rules.builder.value') },
              combinators: { title: t('rules.builder.combinators') },
              addRule: { label: t('rules.builder.addRule'), title: t('rules.builder.addRule') },
              addGroup: { label: t('rules.builder.addGroup'), title: t('rules.builder.addGroup') },
              removeRule: { title: t('rules.builder.removeRule') },
              removeGroup: { title: t('rules.builder.removeGroup') },
              notToggle: { label: t('rules.builder.not'), title: t('rules.builder.not') },
            }}
          />
        </QueryBuilderMaterial>
      </ThemeProvider>
    </>
  )
}
